import { House, User } from '@/client';
import { LuCrown, LuEye, LuPencil, LuShield } from 'react-icons/lu';
import type { IconType } from 'react-icons';

export { hasIncompleteProfile } from '@/utils/userUtils';

export const UserStatuses = {
	Added: 'added',
	Invited: 'invited',
	Registered: 'registered',
	Expired: 'expired',
} as const;
export type UserStatus = (typeof UserStatuses)[keyof typeof UserStatuses];

export const Roles = {
	/** Admin role with full permissions. Not associated with a property */
	Admin: 'admin',
	/** Property owner role (was "homeowner") */
	Owner: 'owner',
	/** Contributor role (was "professional"/"builder") */
	Contributor: 'contributor',
	/** Viewer role (was "guest"/"tradesman") */
	Viewer: 'viewer',
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

/** Roles that can be assigned when sharing access to a property */
export const ASSIGNABLE_ROLES = [Roles.Admin, Roles.Contributor, Roles.Viewer] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

/** Roles assignable via QR code scanning */
export const QR_CODE_ASSIGNABLE_ROLES = [Roles.Contributor, Roles.Viewer] as const;
export type QRCodeAssignableRole = (typeof QR_CODE_ASSIGNABLE_ROLES)[number];

/**
 * Roles assignable on an archived (retained) copy — Viewer only. An archive is a frozen snapshot
 * the professional keeps after transferring the property away: it grants no elevated access and has
 * no QR code, so access there can only ever be view-only.
 */
export const ARCHIVE_ASSIGNABLE_ROLES = [Roles.Viewer] as const;

/** Definition for a role including its label, description, and icon */
export interface RoleDefinition<R extends Role = Role> {
	value: R;
	label: string;
	description: string;
	icon: IconType;
}

/** Complete role definitions — single source of truth for labels, descriptions, and icons */
export const ROLE_DEFINITIONS: Record<Role, RoleDefinition> = {
	[Roles.Owner]: { value: Roles.Owner, label: 'Owner', description: 'Full control of the property', icon: LuCrown },
	[Roles.Admin]: { value: Roles.Admin, label: 'Admin', description: 'Manage access and permissions', icon: LuShield },
	[Roles.Contributor]: {
		value: Roles.Contributor,
		label: 'Contributor',
		description: 'Upload files and add to records',
		icon: LuPencil,
	},
	[Roles.Viewer]: { value: Roles.Viewer, label: 'Viewer', description: 'View records only', icon: LuEye },
} as const;

/** Role options available when granting or editing user access */
export const ASSIGNABLE_ROLE_OPTIONS: readonly RoleDefinition<AssignableRole>[] = ASSIGNABLE_ROLES.map(
	(role) => ROLE_DEFINITIONS[role] as RoleDefinition<AssignableRole>,
);

/** Role options available for QR code scanning */
export const QR_CODE_ROLE_OPTIONS: readonly RoleDefinition<QRCodeAssignableRole>[] = QR_CODE_ASSIGNABLE_ROLES.map(
	(role) => ROLE_DEFINITIONS[role] as RoleDefinition<QRCodeAssignableRole>,
);

/** Role options available when editing access on an archived copy (Viewer only). */
export const ARCHIVE_ROLE_OPTIONS: readonly RoleDefinition<AssignableRole>[] = ARCHIVE_ASSIGNABLE_ROLES.map(
	(role) => ROLE_DEFINITIONS[role] as RoleDefinition<AssignableRole>,
);

/** Default role used as fallback for assignable role selectors */
export const DEFAULT_ASSIGNABLE_ROLE = ROLE_DEFINITIONS[Roles.Viewer];

/** Returns the icon component for a given role */
export const getRoleIcon = (role: string): IconType | null => {
	return ROLE_DEFINITIONS[role as Role]?.icon ?? null;
};

/** Returns the label for a given role */
export const getRoleLabel = (role: string): string => {
	return ROLE_DEFINITIONS[role as Role]?.label ?? role;
};

// Backward-compat aliases
/** @deprecated Use Roles.Owner */
export const Homeowner = Roles.Owner;
/** @deprecated Use Roles.Contributor */
export const Builder = Roles.Contributor;

/**
 * Admin check uses is_admin boolean instead of role field
 */
export const hasAdminRole = (user?: User): boolean => {
	return !!user && user.is_admin === true;
};
const _hasPropertyRole = (house: House, role: Role): boolean => {
	return house.user_role?.role === role;
};

export const hasOwnerRole = (house: House, user: User | null): boolean => {
	return !!user && _hasPropertyRole(house, Roles.Owner);
};

export const hasContributorRole = (house: House, user: User | null): boolean => {
	return !!user && _hasPropertyRole(house, Roles.Contributor);
};

export const hasAdminRoleInProperty = (house: House, user: User | null): boolean => {
	return !!user && _hasPropertyRole(house, Roles.Admin);
};

/** @deprecated Use hasContributorRole */
export const hasProfessionalRole = hasContributorRole;

export const hasViewerRoleInProperty = (house: House, user: User | null): boolean => {
	return !!user && _hasPropertyRole(house, Roles.Viewer);
};

/** @deprecated Use hasViewerRoleInProperty */
export const hasGuestRoleInProperty = hasViewerRoleInProperty;
/**
 * Check if user is an owner (owner or contributor) for the given house
 */
export const isOwner = (house: House, user: User | null): boolean => {
	return hasOwnerRole(house, user) || hasContributorRole(house, user);
};
